import type { FC } from "react";
import { useRef } from "react";

import { createMergedStickAttribute } from "@locators/components/shared/components/StoreLocatorCard/components/StoreLocatorAvailableProducts/utils";
import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";
import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import classNames from "classnames";

import type { StoreLocatorProductAttribute } from "@jti/fe-common";

import { StoreLocatorProductAttributeValues } from "./components/StoreLocatorProductAttributeValues";
import { StoreLocatorStickAttributeValues } from "./components/StoreLocatorStickAttributeValues";

import styles from "./StoreLocatorAvailableProducts.module.scss";

type StoreLocatorAvailableProductsProps = {
  isListView: boolean;
  productAttributes: StoreLocatorProductAttribute[];
};

export const StoreLocatorAvailableProducts: FC<StoreLocatorAvailableProductsProps> = ({ productAttributes }) => {
  const { __ } = useLocatorTranslations();
  const { isAvailableDevicesEnabled, isAvailableSticksEnabled, isMultiBrandSticksEnabled } = useStoreLocatorContext();
  const checkIsAttributeAvailable = (category: string) => isAvailableDevicesEnabled && category === "Product";
  const checkIsStickAttributeAvailable = (category: string) => isAvailableSticksEnabled && category === "Stick";

  const attributes = productAttributes.filter(({ category }) => checkIsAttributeAvailable(category));
  const stickAttributes = productAttributes.filter(({ category }) => checkIsStickAttributeAvailable(category));
  const mergedStickAttribute = createMergedStickAttribute(stickAttributes, isMultiBrandSticksEnabled ?? false);
  const availableText = __("store_locator.popup.available_products.available");

  const allAttributes = [...attributes, ...mergedStickAttribute];
  const titleRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={classNames(styles.productAttributes)} ref={containerRef}>
      {allAttributes.map((attribute) => {
        const categoryText = attribute.category
          ? __(`store_locator.popup.available_products.${attribute.category}`)
          : "";
        const keyText =
          attribute.key && attribute.key !== "Sticks"
            ? __(`store_locator.popup.available_products.${attribute.key}`)
            : "";

        return (
          <div key={attribute.key} className={styles.locatorV2ProductAttributeItem}>
            <h3
              className={classNames(styles.attributeName, styles.locatorV2attributeName)}
              data-testid="attributeName"
              ref={titleRef}
            >
              {availableText} {categoryText} {keyText}
            </h3>
            {attribute.category === "Stick" ? (
              <StoreLocatorStickAttributeValues
                values={attribute.values}
                titleRef={titleRef}
                containerRef={containerRef}
              />
            ) : (
              <StoreLocatorProductAttributeValues
                values={attribute.values}
                titleRef={titleRef}
                containerRef={containerRef}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
